// Campo dei Roster sviluppo (la "Top 11" dello scouting): posti dal modulo, stack in
// profondità per posto; la panchina è il resto della rosa. Gli schieramenti correnti
// usano le API condivise tramite rosterLineupData; nessun recupero browser automatico.

import moment from "moment";

import {DEPARTMENTS, playerDepartment, roleCode} from "@/utils/scoutingData";

const ROLE_DEPARTMENT = {
	POR: "P",
	TD: "D",
	TS: "D",
	DC: "D",
	QD: "D",
	QS: "D",
	MED: "C",
	REG: "C",
	MEZ: "C",
	TRQ: "C",
	ED: "A",
	ES: "A",
	SP: "A",
	PC: "A",
};

export const lineupRoleDepartment = (code) => ROLE_DEPARTMENT[code];

// Ruoli vicini sulla stessa fascia: le ali scendono sui quinti, mai sui terzini.
const NEAR_ROLES = {
	TD: ["QD"],
	QD: ["TD", "ED"],
	ED: ["QD"],
	TS: ["QS"],
	QS: ["TS", "ES"],
	ES: ["QS"],
	SP: ["TRQ"],
	TRQ: ["SP"],
};

const isNear = (code, slot) => !!NEAR_ROLES[code]?.includes(slot.code);

const LINE_Y = {gk: 91, df: 74, dm: 61, cm: 47, am: 32, fw: 15};

const defenders = (n) =>
	({
		3: ["DC", "DC", "DC"],
		4: ["TD", "DC", "DC", "TS"],
		5: ["QD", "DC", "DC", "DC", "QS"],
	})[n] || Array(n).fill("DC");

const midfielders = (n, threeAtBack) => {
	if (n === 1) return ["REG"];
	if (n === 2) return ["MEZ", "MEZ"];
	if (n === 3) return ["MEZ", "REG", "MEZ"];
	if (n === 4)
		return threeAtBack
			? ["QD", "MEZ", "MEZ", "QS"]
			: ["ED", "MEZ", "MEZ", "ES"];
	if (n === 5) return ["ED", "MEZ", "REG", "MEZ", "ES"];
	return Array(n).fill("MEZ");
};

const attackingMidfielders = (n) =>
	({1: ["TRQ"], 2: ["TRQ", "TRQ"], 3: ["ED", "TRQ", "ES"]})[n] ||
	Array(n).fill("TRQ");

const attackers = (n) =>
	({1: ["PC"], 2: ["SP", "PC"], 3: ["ED", "PC", "ES"]})[n] ||
	Array(n).fill("PC");

// Posti del modulo dai conteggi per reparto (stessi campi di /Modules).
export const buildModuleSlots = (module) => {
	const df = module?.defenders ?? 4;
	const lines = [
		["gk", ["POR"]],
		["df", defenders(df)],
		["dm", Array(module?.defensiveMidfielders ?? 0).fill("MED")],
		["cm", midfielders(module?.centralMidfielders ?? 0, df === 3)],
		["am", attackingMidfielders(module?.attackingMidfielders ?? 0)],
		["fw", attackers(module?.attackers ?? 0)],
	];
	return lines.flatMap(([line, codes]) =>
		codes.map((code, i) => ({
			id: line + (i + 1),
			code,
			department: ROLE_DEPARTMENT[code],
			// Da destra a sinistra come in un campo visto dalla propria porta.
			x: Math.round(((codes.length - i) * 100) / (codes.length + 1)),
			y: LINE_Y[line],
		})),
	);
};

// Al cambio di modulo gli stack vanno sul posto con lo stesso codice, poi su un ruolo
// vicino della stessa fascia, poi su uno dello stesso reparto; gli altri in panchina.
export const reassignStacks = (oldSlots, stacks, newSlots) => {
	const next = Object.fromEntries(newSlots.map((s) => [s.id, []]));
	let pending = oldSlots
		.map((slot) => ({slot, players: stacks?.[slot.id] || []}))
		.filter((p) => p.players.length);
	const place = (match) => {
		pending = pending.filter((p) => {
			const target = newSlots.find(
				(s) => !next[s.id].length && match(p.slot, s),
			);
			if (!target) return true;
			next[target.id] = p.players;
			return false;
		});
	};
	place((a, b) => a.code === b.code);
	place((a, b) => isNear(a.code, b));
	place((a, b) => a.department === b.department);
	return {stacks: next, bench: pending.flatMap((p) => p.players)};
};

export const promote = (stack, index) => {
	if (index <= 0 || index >= stack.length) return stack;
	const next = [...stack];
	[next[index - 1], next[index]] = [next[index], next[index - 1]];
	return next;
};

export const usedPlayerIds = (stacks) =>
	new Set(Object.values(stacks || {}).flat());

const byName = (a, b) =>
	(a.lastName || "").localeCompare(b.lastName || "", "it") ||
	(a.firstName || "").localeCompare(b.firstName || "", "it");

const playerCode = (player) =>
	player?.internalRole?.code ||
	(player?.goalkeeper
		? "POR"
		: roleCode(player?.secondaryRole) || roleCode(player?.primaryRole));

// Primo schieramento: ogni giocatore sul posto libero con il suo codice, poi su un ruolo
// vicino, poi su uno del suo reparto; gli altri restano in panchina.
export const autoAssign = (slots, players) => {
	const stacks = Object.fromEntries(slots.map((s) => [s.id, []]));
	let pending = [...(players || [])].sort(byName);
	const place = (match) => {
		pending = pending.filter((player) => {
			const target = slots.find(
				(s) => !stacks[s.id].length && match(player, s),
			);
			if (!target) return true;
			stacks[target.id] = [player.id];
			return false;
		});
	};
	place((player, slot) => playerCode(player) === slot.code);
	place((player, slot) => isNear(playerCode(player), slot));
	place((player, slot) => playerDepartment(player) === slot.department);
	return stacks;
};

const DEPARTMENT_ORDER = DEPARTMENTS.map((d) => d.id);

// Panchina per reparto (P, D, C, A, poi senza ruolo), quindi per cognome.
export const sortBench = (players) =>
	[...(players || [])].sort((a, b) => {
		const rank = (p) => {
			const index = DEPARTMENT_ORDER.indexOf(playerDepartment(p));
			return index < 0 ? DEPARTMENT_ORDER.length : index;
		};
		return rank(a) - rank(b) || byName(a, b);
	});

export const playerAge = (player) =>
	player?.dateOfBirth
		? moment().diff(moment(player.dateOfBirth), "years")
		: null;

// Prima i monitorati, poi gli altri in ordine alfabetico.
export const sortCandidates = (players, monitoredIds) =>
	[...(players || [])].sort(
		(a, b) =>
			(monitoredIds.has(b.id) ? 1 : 0) - (monitoredIds.has(a.id) ? 1 : 0) ||
			byName(a, b),
	);
